import type { ThemeOptionsCustom } from "@circle-react/providers/Theme";
import type {
  Appearance,
  StoredAppearancePreference,
} from "@circle-react/providers/Theme/themes/constants";
import type {
  AppearanceColor,
  ThemeCustomColors,
  ThemeObject,
} from "@circle-react/types/Theme";
import { createContext, useContext, useMemo } from "react";

export interface ThemeContextProps {
  brandColor: AppearanceColor;
  brandTextColor: AppearanceColor;
  clearAppearance?: () => void;
  currentAppearance: Appearance;
  currentTheme: ThemeOptionsCustom;
  customColors: ThemeCustomColors;
  isCustomTheme: boolean;
  isDarkAppearance?: boolean;
  isLightAppearance?: boolean;
  setAppearance?: (appearance: Exclude<StoredAppearancePreference, "">) => void;
  /** Raw stored preference in localStorage. */
  storedPreference?: StoredAppearancePreference;
  toggleAppearance?: () => void;
}

export const ThemeContext = createContext<ThemeContextProps | null>(null);
ThemeContext.displayName = "ThemeContext";

export const useThemeContext = () => {
  const context: ThemeContextProps | null = useContext(ThemeContext);

  if (context === null) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }

  return context;
};

export const useThemeKey = (): "dark" | "light" => {
  const { isDarkAppearance } = useThemeContext();
  return isDarkAppearance ? "dark" : "light";
};

export const useThemeObject = () => {
  const {
    currentTheme,
    customColors,
    currentAppearance,
    isCustomTheme,
    brandColor,
    brandTextColor,
  } = useThemeContext();

  return useMemo<ThemeObject>(
    () => ({
      current_theme: currentTheme,
      custom_colors: customColors,
      default_appearance: currentAppearance,
      is_custom: isCustomTheme,
      brand_color: brandColor,
      brand_text_color: brandTextColor,
    }),
    [
      currentTheme,
      customColors,
      currentAppearance,
      isCustomTheme,
      brandColor,
      brandTextColor,
    ],
  );
};
